import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import {
  UIField,
  UIForm,
  settingsFieldTypes,
  RESERVED_SETTINGS_GIVEN_NAMES,
} from "./allData";
import { Button } from "@components/ui/button";
import { toast } from "@components/ui/use-toast";
import { Trash, CirclePlus, SquarePen, Info, GripVertical } from "lucide-react";
import CustomSelect from "@components/common/CustomSelect";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import InfoTooltip from "@components/common/InfoTooltip";
import { fetchLogicPageInfo } from "./createPluginApi";
import { useQuery } from "react-query";
import PageLoader from "@components/loaders/PageLoader";
import CustomTooltip from "@components/common/CustomTooltip";
import Confirmation from "@components/common/ConfirmDialog";
import { getFieldIconbyFieldType } from "./logicUtils";

interface AddFormDialogProps {
  open: boolean;
  handleClose: () => void;
  addForm: (form: UIForm) => void;
  editForm?: (form: UIForm) => void;
  initialFormData?: UIForm;
  isEditing?: boolean;
  logicType?: any;
  definedVariables?: any;
  pluginId?: string;
  setSelectedBundle?: (bundleId: string) => void;
  setSelectedRequest?: (requestId: string) => void;
  selectedBundle?: string;
  selectedRequest?: string;
  meshVersion?: string;
  settingsWorkflows?: any[];
}

const AddFormDialog: React.FC<AddFormDialogProps> = ({
  open,
  handleClose,
  addForm,
  editForm,
  initialFormData,
  isEditing,
  definedVariables,
  pluginId,
  setSelectedBundle,
  setSelectedRequest,
  selectedBundle,
  selectedRequest,
  meshVersion,
  settingsWorkflows,
}) => {
  const SELECT_PLACEHOLDER = "__select__";
  const [formTitle, setFormTitle] = React.useState("");
  const [userAction, setUserAction] = React.useState("");
  // Always start with Title field unless editing
  const [fields, setFields] = React.useState<UIField[]>([
    {
      fieldId: 0,
      fieldTitle: "Title",
      givenName: "title",
      fieldType: "title",
      placeholder: "",
      default: "",
      infoIcon: "",
      field_subtype: "",
      values: [],
    },
  ]);
  const [tempField, setTempField] = React.useState<Partial<UIField>>({
    fieldTitle: "",
    givenName: "",
    fieldType: "",
    placeholder: "",
    default: "",
    infoIcon: "",
    values: [],
  });
  const [dropdownOption, setDropdownOption] = React.useState({
    displayName: "",
    value: "",
  });
  const [isFromVariable, setIsFromVariable] = React.useState(false);
  const [selectedSettingsBundleId, setSelectedSettingsBundleId] =
    React.useState<string | null>(null);
  const [selectedVariable, setSelectedVariable] = React.useState<string>("");
  const [labelKey, setLabelKey] = React.useState<string>("");
  const [valueKey, setValueKey] = React.useState<string>("");
  const [availableKeys, setAvailableKeys] = React.useState<string[]>([]);
  const [availableBundles, setAvailableBundles] = React.useState<
    { id: string; name: string }[]
  >([]);
  const [availableRequests, setAvailableRequests] = React.useState<any[]>([]);
  const [editFieldId, setEditFieldId] = React.useState<number | null>(null);
  const [draggedFieldId, setDraggedFieldId] = React.useState<number | null>(
    null,
  );
  const [dragOverFieldId, setDragOverFieldId] = React.useState<number | null>(
    null,
  );

  const isEditMode = React.useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("edit") === "1";
  }, []);

  const shouldFetchLogicPageInfo = React.useMemo(() => {
    if (!pluginId || !open) return false;

    // Fetch only when logic bundle data is actually required in this dialog.
    return (
      tempField.fieldType === "testConnectionBtn" ||
      (tempField.fieldType === "dropdown" && isFromVariable)
    );
  }, [pluginId, open, isEditMode, tempField.fieldType, isFromVariable]);

  const [isDeleteField, setIsDeleteField] = React.useState(false);
  const [deleteFieldId, setDeleteFieldId] = React.useState<number | null>(null);

  const normalizeGivenName = (value?: string) =>
    String(value || "")
      .trim()
      .toLowerCase();

  const reservedGivenNames = React.useMemo(
    () => new Set([...RESERVED_SETTINGS_GIVEN_NAMES, "submit"]),
    [],
  );

  const bundleScopedVariables = React.useMemo(() => {
    if (!selectedSettingsBundleId) return [];
    const selectedId = String(selectedSettingsBundleId);

    return (definedVariables || []).filter((variable: any) => {
      const variableBundleId = String(
        variable?.sourceBundleId ??
          variable?.bundleId ??
          variable?.bundle_id ??
          "",
      );
      return variableBundleId === selectedId;
    });
  }, [definedVariables, selectedSettingsBundleId]);

  // const hasLogicPage = React.useMemo(() => {
  //   if (!pluginId) return false;
  //   const allBundles = logicPageInfo?.data?.LogicBundles || [];
  //   return allBundles.some((bundle: any) => bundle.bundleType === "setting");
  // }, [pluginId]);

  // console.log(hasLogicPage,'   ')

  // Fetch logic page info for bundles and requests
  const { data: logicPageInfo, isLoading: isLogicLoading } = useQuery({
    queryKey: ["logicPageInfo", pluginId],
    queryFn: async () => {
      if (!pluginId) {
        return { data: { LogicBundles: [] } };
      }

      try {
        const result = await fetchLogicPageInfo(pluginId);
        const isLogicPageMissing =
          result?.status === 0 &&
          String(result?.message || "")
            .toLowerCase()
            .includes("logic page file not found");

        if (isLogicPageMissing) {
          return { data: { LogicBundles: [] } };
        }

        return result;
      } catch (error: any) {
        const apiMessage = String(
          error?.response?.data?.message || error?.message || "",
        ).toLowerCase();

        if (apiMessage.includes("logic page file not found")) {
          return { data: { LogicBundles: [] } };
        }

        throw error;
      }
    },
    staleTime: 0,
    enabled: shouldFetchLogicPageInfo,
    cacheTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  // Extract  bundles and their requests for test connection
  React.useEffect(() => {
    // For mesh version 2.0, use settingsWorkflows instead of bundles
    if (meshVersion === "2.0") {
      if (!settingsWorkflows || settingsWorkflows.length === 0) {
        setAvailableBundles([]);
        return;
      }

      const workflows = settingsWorkflows.map((workflow: any) => ({
        id: workflow.uid,
        name: workflow.title,
      }));

      setAvailableBundles(workflows);
      return;
    }

    // For mesh version 1.0, use bundles
    if (!logicPageInfo?.data?.LogicBundles?.length) {
      setAvailableBundles([]);
      return;
    }

    // to get all bundles
    // const allBundles = logicPageInfo.data.LogicBundles.map(
    //   (bundle: any) => bundle,
    // );

    // filter only settings bundles
    const settingsBundles = logicPageInfo.data.LogicBundles.filter(
      (bundle: any) => bundle.bundleType == "setting",
    );

    const bundles = settingsBundles.map((bundle: any) => ({
      id: bundle.bundleId,
      name: bundle.bundleTitle,
    }));

    setAvailableBundles(bundles);
  }, [logicPageInfo, meshVersion, settingsWorkflows]);

  // Handle settings bundle selection
  const handleSelectedBundle = (value: string) => {
    setSelectedBundle?.(value);

    // For mesh version 2.0, workflows don't have requests (each workflow is standalone)
    if (meshVersion === "2.0") {
      setAvailableRequests([]);
      return;
    }

    // For mesh version 1.0, use bundles with requests
    if (!logicPageInfo?.data?.LogicBundles) {
      setAvailableRequests([]);
      return;
    }

    // Filter only settings bundles
    const settingsBundles = logicPageInfo.data.LogicBundles.filter(
      (bundle: any) => bundle.bundleType == "setting",
    );

    // find the bundle with selected id
    const bundle = settingsBundles.find(
      (b: any) => String(b.bundleId) == String(value),
    );

    if (bundle?.requests) {
      const requests = bundle.requests.map((req: any) => req);
      setAvailableRequests(requests);
    } else {
      setAvailableRequests([]);
    }
  };

  // Handle given name change with formatting
  const handleGivenNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
      .replace(/\s+/g, "_")
      .replace(/[^a-z_]/gi, "")
      .toLowerCase();
    setTempField({ ...tempField, givenName: value });
  };

  // Handle request selection
  const handleSelectRequest = (value: string) => {
    const currentRequest = availableRequests.find(
      (req) => String(req.requestId) == String(value),
    );
    // take the request from the selected bundle according to request id
    if (currentRequest) {
      setSelectedRequest?.(currentRequest.requestId);
    } else {
      setSelectedRequest?.("");
    }
  };

  // Populate form data when editing
  React.useEffect(() => {
    if (initialFormData) {
      setFormTitle(initialFormData.formTitle || "");
      setUserAction(initialFormData.userAction?.action || "");
      setFields(initialFormData.fieldList || []);
    } else {
      // Reset fields to default Title field when not editing
      setFields([
        {
          fieldId: 0,
          fieldTitle: "Title",
          givenName: "title",
          fieldType: "title",
          placeholder: "",
          default: "",
          infoIcon: "",
          field_subtype: "",
          values: [],
        },
      ]);
    }
  }, [initialFormData]);

  // clear the bundle and request if its not editing testConnectionBtn
  React.useEffect(() => {
    if (!isEditing) return;
    const hasTestConnectionBtn = fields.some(
      (f) => f.fieldType === "testConnectionBtn",
    );
    if (!hasTestConnectionBtn) {
      setSelectedBundle?.("");
      setSelectedRequest?.("");
    }
  }, [fields, isEditing]);

  // Improved addField with duplicate check
  const addField = () => {
    const requiredFields = ["fieldTitle", "givenName", "fieldType"] as const;

    for (const key of requiredFields) {
      if (!tempField[key]) {
        toast({
          variant: "destructive",
          title: "Missing Required Field",
          description: `${key} is required.`,
        });
        return;
      }
    }

    // Prevent adding another Title field
    if (tempField.givenName === "title" || tempField.fieldType === "title") {
      toast({
        title: "Title Field",
        variant: "destructive",
        description:
          "Title field is already present as default and cannot be added again.",
        duration: 3000,
      });
      return;
    }

    const needsOptions = ["checkbox", "radio", "dropdown"].includes(
      String(tempField.fieldType || ""),
    );
    const hasOptions =
      Array.isArray(tempField.values) && tempField.values.length > 0;

    if (needsOptions && !hasOptions) {
      toast({
        variant: "destructive",
        title: "Missing Options",
        description:
          "Please add at least one option manually or generate options from a variable before adding this field.",
        duration: 3000,
      });
      return;
    }

    const normalizedGivenName = normalizeGivenName(tempField.givenName);
    const normalizedFieldTitle = normalizeGivenName(tempField.fieldTitle);
    const normalizedFieldType = normalizeGivenName(tempField.fieldType);
    const isSubmitFieldType = normalizedFieldType === "submit";
    const isSubmitGivenName = normalizedGivenName === "submit";
    const isSubmitFieldTitle = normalizedFieldTitle === "submit";
    const isReservedGivenName = reservedGivenNames.has(normalizedGivenName);
    const isAllowedSystemField = normalizedGivenName === normalizedFieldType;

    if ((isSubmitGivenName || isSubmitFieldTitle) && !isSubmitFieldType) {
      toast({
        title: "Reserved Submit Name",
        variant: "destructive",
        description:
          "Submit/submit is allowed only when Field Type is Action Submission Button.",
        duration: 3000,
      });
      return;
    }

    if (isReservedGivenName && !isAllowedSystemField) {
      toast({
        title: "Reserved Given Name",
        variant: "destructive",
        description: `Given name '${tempField.givenName}' is reserved. Please use a different value.`,
        duration: 3000,
      });
      return;
    }

    const duplicate = fields.find(
      (field) =>
        field.givenName &&
        tempField.givenName &&
        normalizeGivenName(field.givenName) === normalizedGivenName,
    );

    if (duplicate && !editFieldId) {
      toast({
        title: "Duplicate Field",
        variant: "destructive",
        description: `field with the same given_name already exists.`,
        duration: 3000,
      });
      return;
    }

    if (tempField.fieldType === "dropdown" && isFromVariable) {
      if (!selectedSettingsBundleId || !selectedVariable) {
        toast({
          variant: "destructive",
          title: "Missing Variable Source",
          description: "Please select a settings bundle and a variable.",
        });
        return;
      }

      if (!tempField.values || tempField.values.length === 0) {
        toast({
          variant: "destructive",
          title: "Missing Dropdown Options",
          description:
            "Generate dropdown options from the selected variable before adding the field.",
        });
        return;
      }
    }

    if (editFieldId) {
      //  Update the existing field instead of adding a new one
      // Prevent editing Title field
      if (
        fields.find((f) => f.fieldId === editFieldId && f.givenName === "title")
      ) {
        toast({
          title: "Title Field",
          variant: "destructive",
          description: "Title field cannot be edited.",
          duration: 3000,
        });
        setEditFieldId(null);
        setTempField({
          fieldTitle: "",
          givenName: "",
          fieldType: "",
          placeholder: "",
          default: "",
          infoIcon: "",
          values: [],
        });
        return;
      }
      const updatedFields: any = fields.map((f) =>
        f.fieldId === editFieldId
          ? {
              ...f,
              ...tempField,
              ...(tempField.fieldType === "testConnectionBtn" && {
                bundleId: selectedBundle || "",
                requestId: selectedRequest || "",
              }),
              ...(tempField.fieldType === "dropdown" && {
                fromVariable: isFromVariable,
                sourceBundleId: isFromVariable
                  ? String(selectedSettingsBundleId || "")
                  : "",
                selectedVariable: isFromVariable ? selectedVariable : "",
                labelKey: isFromVariable ? labelKey : "",
                valueKey: isFromVariable ? valueKey : "",
              }),
            }
          : f,
      );
      toast({
        variant: "success",
        title: `Field ' ${tempField.fieldTitle} ' updated`,
        description: `Field ' ${tempField.fieldTitle}[' updated successfully in settings ${formTitle}.`,
        duration: 3000,
      });
      setFields(updatedFields);
      setEditFieldId(null);
    } else {
      const field_subtype = (tempField.fieldType as string).includes("_")
        ? (tempField.fieldType as string).split("_")[1]
        : "";

      const newField: UIField = {
        fieldId: Date.now(),
        fieldTitle: tempField.fieldTitle!,
        givenName: tempField.givenName!,
        fieldType: tempField.fieldType!,
        placeholder: tempField.placeholder || "",
        default: tempField.default || "",
        infoIcon: tempField.infoIcon || "",
        field_subtype: field_subtype || "",
        values: tempField.values || [],
        ...(tempField.fieldType === "testConnectionBtn" && {
          bundleId: selectedBundle || "",
          requestId: selectedRequest || "",
        }),
        ...(tempField.fieldType === "dropdown" && {
          fromVariable: isFromVariable,
          sourceBundleId: isFromVariable
            ? String(selectedSettingsBundleId || "")
            : "",
          selectedVariable: isFromVariable ? selectedVariable : "",
          labelKey: isFromVariable ? labelKey : "",
          valueKey: isFromVariable ? valueKey : "",
        }),
      };

      setFields((prev) => [...prev, newField]);
      toast({
        variant: "success",
        title: `Field ' ${newField.fieldTitle} ' added`,
        description: `Field ' ${newField.fieldTitle} ' added successfully in settings ${formTitle}.`,
        duration: 3000,
      });
    }

    // Clear tempField and selections
    setTempField({
      fieldTitle: "",
      givenName: "",
      fieldType: "",
      placeholder: "",
      default: "",
      infoIcon: "",
      values: [],
    });
    // Clear bundle and request selections after adding field
    setSelectedBundle?.("");
    setSelectedRequest?.("");
    setAvailableRequests([]);
    setIsFromVariable(false);
    setSelectedSettingsBundleId(null);
    setSelectedVariable("");
    setAvailableKeys([]);
    setLabelKey("");
    setValueKey("");
  };

  const clearField = () => {
    setTempField({
      fieldTitle: "",
      givenName: "",
      fieldType: "",
      placeholder: "",
      default: "",
      infoIcon: "",
      values: [],
    });
    setEditFieldId(null);
    // Clear bundle and request selections
    setSelectedBundle?.("");
    setSelectedRequest?.("");
    setAvailableRequests([]);
    setIsFromVariable(false);
    setSelectedSettingsBundleId(null);
    setSelectedVariable("");
    setAvailableKeys([]);
    setLabelKey("");
    setValueKey("");
  };

  // edit field
  const editField = (id: number) => {
    const item: any = fields?.find((itm: any) => itm?.fieldId == id);

    if (!item) return;

    // Set the selected bundle and request if it's a testConnectionBtn BEFORE setting tempField
    if (item.fieldType === "testConnectionBtn") {
      // Prefill bundle & request from saved field
      const savedBundleId = String(item.bundleId || "");
      const savedRequestId = String(item.requestId || "");

      // Check if the saved bundle still exists in available bundles
      const bundleExists = availableBundles.some(
        (bundle) => String(bundle.id) === savedBundleId,
      );

      // Only set the bundle/request if it exists, otherwise reset to empty
      if (bundleExists && savedBundleId) {
        setSelectedBundle?.(savedBundleId);

        // For mesh version 2.0, skip request handling
        if (meshVersion === "2.0") {
          setSelectedRequest?.("");
          setAvailableRequests([]);
        } else {
          // For mesh version 1.0, handle requests
          setSelectedRequest?.(savedRequestId);

          // Prefill bundle & request from logicPageInfo (settings bundles only)
          const settingsBundles = (
            logicPageInfo?.data?.LogicBundles || []
          ).filter((bundle: any) => bundle.bundleType == "setting");
          const selectedBundleData = settingsBundles.find(
            (b: any) => String(b.bundleId) === savedBundleId,
          );

          // Populate available requests for the bundle
          if (selectedBundleData?.requests) {
            const requests = selectedBundleData.requests.map((r: any) => r);
            setAvailableRequests(requests);
          } else {
            setAvailableRequests([]);
          }
        }
      } else {
        // Bundle no longer exists, reset selections
        setSelectedBundle?.("");
        setSelectedRequest?.("");
        setAvailableRequests([]);
      }
    }

    if (item.fieldType === "dropdown" && item.fromVariable) {
      const sourceBundleId = String(item.sourceBundleId || "");
      const variableName = String(item.selectedVariable || "");

      setIsFromVariable(true);
      setSelectedSettingsBundleId(sourceBundleId || null);
      setSelectedVariable(variableName);
      setLabelKey(String(item.labelKey || ""));
      setValueKey(String(item.valueKey || ""));

      const selectedVar = (definedVariables || []).find(
        (v: any) => String(v?.name) === variableName,
      );

      if (selectedVar) {
        const rawData = selectedVar.value;
        if (
          typeof rawData === "string" ||
          typeof rawData === "number" ||
          typeof rawData === "boolean"
        ) {
          setAvailableKeys([]);
        } else {
          setAvailableKeys(extractKeysFromData(rawData));
        }
      } else {
        setAvailableKeys([]);
      }
    } else {
      setIsFromVariable(false);
      setSelectedSettingsBundleId(null);
      setSelectedVariable("");
      setAvailableKeys([]);
      setLabelKey("");
      setValueKey("");
    }

    // Set tempField with all properties including bundleId and requestId
    setTempField(item);
    setEditFieldId(id);
  };

  const onDeleteField = () => {
    // Prevent deleting Title field (always index 0)
    if (deleteFieldId === 0 && fields[0]?.givenName === "title") {
      toast({
        title: "Title Field",
        variant: "destructive",
        description: "Title field cannot be deleted.",
        duration: 3000,
      });
      return;
    }
    if (deleteFieldId === null) return;
    const updated = [...fields];
    updated.splice(deleteFieldId, 1);
    setFields(updated);
    toast({
      variant: "success",
      title: "Field Deleted",
      description: "Field deleted successfully.",
      duration: 3000,
    });
    setIsDeleteField(false);
    setDeleteFieldId(null);
  };

  // cancel delete field
  const handleCancelDelete = () => {
    setIsDeleteField(false);
    setDeleteFieldId(null);
  };

  // Delete Field
  const deleteField = (index: number) => {
    setIsDeleteField(true);
    setDeleteFieldId(index);
  };

  const isTitleField = React.useCallback(
    (field: UIField) =>
      String(field?.givenName || "").toLowerCase() === "title",
    [],
  );

  const handleDragStart = (field: UIField) => {
    if (isTitleField(field)) return;
    setDraggedFieldId(field.fieldId);
    setDragOverFieldId(field.fieldId);
  };

  const handleDragOver = (
    event: React.DragEvent<HTMLDivElement>,
    field: UIField,
  ) => {
    if (isTitleField(field) || draggedFieldId === null) return;
    event.preventDefault();

    if (dragOverFieldId !== field.fieldId) {
      setDragOverFieldId(field.fieldId);
    }
  };

  const handleDrop = (field: UIField) => {
    if (
      draggedFieldId === null ||
      draggedFieldId === field.fieldId ||
      isTitleField(field)
    ) {
      setDraggedFieldId(null);
      setDragOverFieldId(null);
      return;
    }

    setFields((prev) => {
      const sourceIndex = prev.findIndex(
        (item) => item.fieldId === draggedFieldId,
      );
      const targetIndex = prev.findIndex(
        (item) => item.fieldId === field.fieldId,
      );

      if (sourceIndex < 0 || targetIndex < 0) return prev;

      const reordered = [...prev];
      const [movedItem] = reordered.splice(sourceIndex, 1);
      reordered.splice(targetIndex, 0, movedItem);

      return reordered;
    });

    setDraggedFieldId(null);
    setDragOverFieldId(null);
  };

  const handleDragEnd = () => {
    setDraggedFieldId(null);
    setDragOverFieldId(null);
  };

  // Handle form submission
  const handleSubmit = () => {
    if (fields.length === 0) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please add at least one field to the form.",
      });
      return;
    }

    if (!formTitle) {
      toast({
        variant: "destructive",
        title: "Missing",
        description: "Form Title is required.",
      });
      return;
    }

    const newForm: UIForm = {
      formTitle,
      userAction: userAction ? { action: userAction } : undefined,
      fieldList: fields,
    };

    if (isEditing && editForm) {
      editForm(newForm);
    } else {
      addForm(newForm);
    }

    setEditFieldId(null);
    handleClose();
  };

  // Add Dropdown Option
  const addDropdownOption = () => {
    if (!dropdownOption.displayName || !dropdownOption.value) {
      toast({
        variant: "destructive",
        title: "Missing Data",
        description: "Both display name and value are required.",
        duration: 3000,
      });
      return;
    }

    const updatedValues = [...(tempField.values || []), dropdownOption];

    const duplicateValue = updatedValues.find(
      (opt, idx) =>
        opt.value === dropdownOption.value && idx !== updatedValues.length - 1,
    );

    if (duplicateValue) {
      toast({
        variant: "destructive",
        title: "Duplicate Value",
        description: "An option with this value already exists.",
      });
      return;
    }

    // Always assign `default: "selected"` to the first item, remove it from others
    const processedValues = updatedValues.map((opt, idx) => ({
      ...opt,
      ...(idx === 0 ? { default: "selected" } : {}),
    }));

    setTempField({
      ...tempField,
      values: processedValues,
    });

    setDropdownOption({ displayName: "", value: "" });
  };

  // to extract keys from nested object
  const extractKeysFromData = (data: any): string[] => {
    const keys = new Set<string>();

    const walk = (item: any) => {
      if (Array.isArray(item)) {
        item.forEach(walk);
      } else if (typeof item === "object" && item !== null) {
        Object.keys(item).forEach((key) => keys.add(key));
        Object.values(item).forEach(walk);
      }
    };

    walk(data);
    return Array.from(keys);
  };

  // normalize nested object to array of objects
  const normalizeToObjectsArray = (input: any): any[] => {
    const result: any[] = [];

    const walk = (data: any) => {
      if (Array.isArray(data)) {
        data.forEach(walk);
      } else if (data && typeof data === "object") {
        const flat: any = {};
        const flatten = (obj: any, prefix = "") => {
          Object.entries(obj).forEach(([k, v]) => {
            const key = prefix ? `${prefix}.${k}` : k;
            if (v && typeof v === "object" && !Array.isArray(v)) {
              flatten(v, key);
            } else {
              flat[key] = Array.isArray(v) ? v.join(", ") : v;
            }
          });
        };
        flatten(data);
        result.push(flat);
      }
    };

    walk(input);
    return result;
  };

  // generate dropdown options from raw data
  const generateDropdownOptions = (
    rawData: any,
    labelKey?: string,
    valueKey?: string,
  ): { label: string; value: string }[] => {
    if (
      typeof rawData === "string" ||
      typeof rawData === "number" ||
      typeof rawData === "boolean"
    ) {
      const val = String(rawData);
      return [{ label: val, value: val }];
    }

    const normalized = normalizeToObjectsArray(rawData);
    const options: { label: string; value: string }[] = [];

    normalized.forEach((item) => {
      if (typeof item === "object") {
        if (labelKey && valueKey && item[labelKey] && item[valueKey]) {
          options.push({
            label: String(item[labelKey]),
            value: String(item[valueKey]),
          });
        } else {
          const keys = Object.keys(item);
          if (keys.length >= 2) {
            options.push({
              label: String(item[keys[0]]),
              value: String(item[keys[1]]),
            });
          } else if (keys.length === 1) {
            const key = keys[0];
            options.push({
              label: String(item[key]),
              value: String(item[key]),
            });
          }
        }
      }
    });

    return options;
  };

  // handle variable change
  const handleVariableChange = (variableName: string) => {
    setSelectedVariable(variableName);
    setLabelKey("");
    setValueKey("");

    const variable = bundleScopedVariables.find(
      (v: any) => String(v?.name) === String(variableName),
    );
    if (!variable) return;

    const rawData = variable.value;
    const name = variable.name;

    // If primitive, generate a single option immediately
    if (
      typeof rawData === "string" ||
      typeof rawData === "number" ||
      typeof rawData === "boolean"
    ) {
      const val = String(rawData);
      setTempField((prev) => ({
        ...prev,
        fromVariable: true,
        sourceBundleId: String(selectedSettingsBundleId || ""),
        selectedVariable: variableName,
        labelKey: "",
        valueKey: "",
        values: [{ displayName: name, value: val, default: "selected" }],
      }));
      setAvailableKeys([]);
      return;
    }

    // Otherwise extract available keys for label/value mapping
    const keys = extractKeysFromData(rawData);
    setAvailableKeys(keys);
  };

  // handle generate options
  const handleGenerateOptions = () => {
    const selectedVar = bundleScopedVariables.find(
      (v: any) => String(v?.name) === String(selectedVariable),
    );
    if (!selectedVar) {
      toast({
        variant: "destructive",
        title: "Variable Not Found",
        description: "Please select a valid variable from the selected bundle.",
      });
      return;
    }

    const options = generateDropdownOptions(
      selectedVar.value,
      labelKey,
      valueKey,
    );

    if (options.length === 0) {
      toast({
        variant: "destructive",
        title: "No Options Generated",
        description:
          "Could not generate dropdown options from the selected variable.",
      });
      return;
    }

    setDropdownOption((prev: any) => ({ ...prev, displayName: "", value: "" }));
    setTempField((prev) => ({
      ...prev,
      fromVariable: true,
      sourceBundleId: String(selectedSettingsBundleId || ""),
      selectedVariable,
      labelKey,
      valueKey,
      values: options.map((opt, index) => ({
        displayName: opt.label,
        value: opt.value,
        ...(index === 0 ? { default: "selected" } : {}),
      })),
    }));
  };

  // Handle field type selection with special handling for testConnectionBtn / proxy
  const handleSelectFieldType = (value: string) => {
    setDropdownOption({
      displayName: "",
      value: "",
    });

    const wasAutoNamedField =
      tempField.fieldType === "testConnectionBtn" ||
      tempField.fieldType === "proxy";

    setTempField((prev) => ({
      ...prev,
      fieldType: value,

      // Clear values whenever type changes
      values: [],

      fieldTitle:
        value === "testConnectionBtn"
          ? "Test Connection"
          : value === "proxy"
            ? "Proxy"
            : wasAutoNamedField
              ? ""
              : prev.fieldTitle,

      givenName:
        value === "testConnectionBtn"
          ? "Test Connection"
          : value === "proxy"
            ? "proxy"
            : wasAutoNamedField
              ? ""
              : prev.givenName,
    }));

    setIsFromVariable(false);
    setSelectedSettingsBundleId(null);
    setSelectedVariable("");
    setAvailableKeys([]);
    setLabelKey("");
    setValueKey("");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={handleClose}
      modal={meshVersion == "2.0" ? true : false}
    >
      <Confirmation
        show={isDeleteField}
        text="Are you sure you want to delete this field?"
        confirmText="Delete"
        rejectText="Cancel"
        confirmAction={() => onDeleteField()}
        rejectAction={() => handleCancelDelete()}
        closeDialog={() => handleCancelDelete()}
      />
      {meshVersion != "2.0" && (
        <div className={"fixed inset-0 z-50 " + (open ? "block" : "hidden")}>
          <div className="relative w-full h-full">
            <div className="absolute inset-0 bg-modalBackground" />
          </div>
        </div>
      )}
      <PageLoader show={isLogicLoading} />
      <DialogContent
        className="max-h-[80vh] overflow-y-auto text-sm max-w-[600px] overflow-x-hidden"
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
      >
        <DialogHeader className="border-b pb-3">
          <DialogTitle className="flex items-center gap-2">
            {isEditing ? "Edit Settings Form" : "Add Settings Form"}
            <InfoTooltip text=" This form is to create the fields for your created settings page. You can add multiple fields of different types. Form Title , Given Name , Field Title and Field Type are required." />
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col">
          <label htmlFor="formTitle" className="text-xs mb-1">
            Form Title *
          </label>
          <input
            className="p-2 w-full border rounded-lg bg-transparent border-primaryBlue text-primaryBlue font-semibold focus:ring-secondary focus:ring-1 "
            placeholder="Form Title (required)"
            value={formTitle}
            onChange={(e) => setFormTitle(e.target.value)}
          />
        </div>

        <label htmlFor="">
          Form fields
          <div className="border-2 border-secondary p-4 rounded-lg">
            <div>
              <div className="mb-1 flex items-center justify-between w-full">
                <label
                  htmlFor="fieldType"
                  className="text-xs  text-muted-foreground"
                >
                  Field Type *
                </label>
                <CustomTooltip
                  text="Select the type of field you want to add."
                  side="left"
                  className="text-xs"
                >
                  <div>
                    <Info size={15} />
                  </div>
                </CustomTooltip>
              </div>
              <CustomSelect
                data={[
                  ...((isEditing &&
                    tempField.fieldType === "testConnectionBtn") ||
                  !fields.some((f) => f.fieldType === "testConnectionBtn")
                    ? [
                        {
                          label: "Test connection - Button",
                          value: "testConnectionBtn",
                        },
                      ]
                    : []),
                  ...settingsFieldTypes.filter(
                    (field) =>
                      field.value !== "proxy" ||
                      (isEditing && tempField.fieldType === "proxy") ||
                      !fields.some((f) => f.fieldType === "proxy"),
                  ),
                ]}
                placeholder="Select Field Type (required)"
                onSelect={(val) => handleSelectFieldType(val)}
                defaultValue={tempField.fieldType ? [tempField.fieldType] : []}
                multiSelect={false}
                sorted={false}
              />
            </div>
            {/* Row 1: Form Title & User Action */}

            {tempField.fieldType !== "testConnectionBtn" &&
              tempField.fieldType !== "proxy" && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div className="col-span-1 ">
                    <label
                      htmlFor="fieldTitle"
                      className="text-xs mb-1 text-muted-foreground flex flex-col"
                    >
                      Field Title *
                    </label>
                    <input
                      className="p-2 w-full border rounded-lg bg-transparent border-secondary mb-3 focus:ring-secondary focus:ring-1"
                      placeholder="Field Title (e.g., IP address)"
                      value={
                        tempField.fieldType === "testConnectionBtn"
                          ? "Test Connection"
                          : tempField.fieldType === "proxy"
                            ? "Proxy"
                            : tempField.fieldTitle
                      }
                      disabled={tempField.fieldType === "testConnectionBtn"}
                      onChange={(e) => {
                        setTempField({
                          ...tempField,
                          fieldTitle: e.target.value,
                        });
                      }}
                    />
                  </div>

                  <div className="col-span-1">
                    <div className="col-span-1">
                      <div className="flex items-center justify-between mb-1">
                        <label
                          htmlFor="givenName"
                          className="text-xs  text-muted-foreground"
                        >
                          Given Name *
                        </label>
                        <InfoTooltip text="Given Name is a unique identifier like value of the FieldTitle for the field, this will be used in the upcoming integrations." />
                      </div>
                      <input
                        className="p-2 border rounded-lg bg-transparent border-secondary w-full focus:ring-secondary focus:ring-1"
                        placeholder="Given Name (required)"
                        value={
                          tempField.fieldType === "testConnectionBtn"
                            ? "Test Connection"
                            : tempField.fieldType === "proxy"
                              ? "proxy"
                              : tempField.givenName
                        }
                        disabled={
                          tempField.fieldType === "testConnectionBtn" ||
                          tempField.fieldType === "proxy"
                        }
                        onChange={handleGivenNameChange}
                      />
                    </div>
                  </div>
                </div>
              )}

            {/* Row 4: Dropdown / Radio / Multi-select / Checkbox Options (if applicable) */}
            {(tempField.fieldType === "dropdown" ||
              tempField.fieldType === "radio" ||
              tempField.fieldType === "multi_select_dropdown" ||
              tempField.fieldType === "checkbox") && (
              <>
                <div className=" border-2 p-3 rounded-lg space-y-2 bg-muted mb-3">
                  <label htmlFor="">
                    {" "}
                    Create fields for [{" "}
                    <strong className="text-primary">
                      {" "}
                      {tempField.fieldTitle
                        ? tempField.fieldTitle
                        : tempField.fieldType}{" "}
                      -{" "}
                      <span className="font-normal text-muted-foreground">
                        {" "}
                        {tempField.fieldType}{" "}
                      </span>
                    </strong>
                    ]
                  </label>

                  {meshVersion !== "2.0" && (
                    <div className="flex items-center">
                      <input
                        id="isFromVariable"
                        type="checkbox"
                        className="w-4 h-4"
                        checked={isFromVariable}
                        onChange={(e) => {
                          const nextValue = e.target.checked;
                          setIsFromVariable(nextValue);
                          setSelectedSettingsBundleId(null);
                          setSelectedVariable("");
                          setAvailableKeys([]);
                          setLabelKey("");
                          setValueKey("");
                          setTempField((prev) => ({
                            ...prev,
                            values: [],
                            fromVariable: nextValue,
                            sourceBundleId: "",
                            selectedVariable: "",
                            labelKey: "",
                            valueKey: "",
                          }));
                        }}
                      />
                      <label className="ml-2 text-sm " htmlFor="isFromVariable">
                        Is from Variable ?
                      </label>
                    </div>
                  )}

                  {!isFromVariable ? (
                    <>
                      <div className="flex items-center gap-2">
                        <input
                          className="flex-1 p-2 border rounded-lg bg-background border-secondary focus:ring-secondary focus:ring-1"
                          placeholder="Display Name"
                          value={dropdownOption.displayName}
                          onChange={(e) =>
                            setDropdownOption({
                              ...dropdownOption,
                              displayName: e.target.value,
                            })
                          }
                        />
                        <input
                          className="flex-1 p-2 border rounded-lg bg-background border-secondary focus:ring-secondary focus:ring-1"
                          placeholder="Value"
                          value={dropdownOption.value}
                          onChange={(e) =>
                            setDropdownOption({
                              ...dropdownOption,
                              value: e.target.value,
                            })
                          }
                        />
                        <Button
                          onClick={addDropdownOption}
                          disabled={
                            !dropdownOption.displayName || !dropdownOption.value
                          }
                        >
                          Add
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-muted-foregroundp-4 ">
                      <div className="flex flex-col gap-1 ">
                        <label>select the settings bundle</label>
                        <Select
                          value={selectedSettingsBundleId || ""}
                          onValueChange={(val) => {
                            if (val === SELECT_PLACEHOLDER) {
                              setSelectedSettingsBundleId(null);
                              setSelectedVariable("");
                              setAvailableKeys([]);
                              setLabelKey("");
                              setValueKey("");
                              setTempField((prev) => ({
                                ...prev,
                                values: [],
                                fromVariable: true,
                                sourceBundleId: "",
                                selectedVariable: "",
                                labelKey: "",
                                valueKey: "",
                              }));
                              return;
                            }

                            setSelectedSettingsBundleId(val);
                            setSelectedVariable("");
                            setAvailableKeys([]);
                            setLabelKey("");
                            setValueKey("");
                            setTempField((prev) => ({
                              ...prev,
                              values: [],
                              fromVariable: true,
                              sourceBundleId: String(val || ""),
                              selectedVariable: "",
                              labelKey: "",
                              valueKey: "",
                            }));
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select Settings Bundle" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={SELECT_PLACEHOLDER}>
                              Select settings bundle
                            </SelectItem>
                            {availableBundles.length > 0 ? (
                              availableBundles.map((bundle) => (
                                <SelectItem
                                  key={String(bundle.id)}
                                  value={String(bundle.id)}
                                >
                                  {bundle.name}
                                </SelectItem>
                              ))
                            ) : (
                              <SelectItem value="none" disabled>
                                No Settings Bundles Available
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Variable Dropdown */}
                      {selectedSettingsBundleId && (
                        <div className="mt-2">
                          <label className="mb-1">Select Variable:</label>
                          <Select
                            value={selectedVariable}
                            onValueChange={(val) => {
                              if (val === SELECT_PLACEHOLDER) {
                                setSelectedVariable("");
                                setAvailableKeys([]);
                                setLabelKey("");
                                setValueKey("");
                                setTempField((prev) => ({
                                  ...prev,
                                  values: [],
                                  selectedVariable: "",
                                  labelKey: "",
                                  valueKey: "",
                                }));
                                return;
                              }

                              handleVariableChange(val);
                            }}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select Variable" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectGroup>
                                <SelectItem value={SELECT_PLACEHOLDER}>
                                  Select variable
                                </SelectItem>
                                {bundleScopedVariables.length > 0 ? (
                                  bundleScopedVariables.map((variable: any) => (
                                    <SelectItem
                                      key={variable.name}
                                      value={variable.name}
                                    >
                                      {variable.name}
                                    </SelectItem>
                                  ))
                                ) : (
                                  <SelectItem value="none" disabled>
                                    No variables found for selected bundle
                                  </SelectItem>
                                )}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Label & Value Key Selectors */}
                      {availableKeys.length > 0 && (
                        <div className="mt-2">
                          <label className="self-center ">
                            Label & Value Keys:
                          </label>
                          <div className="flex gap-2 mt-2">
                            <Select
                              value={labelKey}
                              onValueChange={(val) =>
                                setLabelKey(
                                  val === SELECT_PLACEHOLDER ? "" : val,
                                )
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Label Key" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={SELECT_PLACEHOLDER}>
                                  Select label key
                                </SelectItem>
                                {availableKeys.map((k) => (
                                  <SelectItem key={k} value={k}>
                                    {k}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>

                            <Select
                              value={valueKey}
                              onValueChange={(val) =>
                                setValueKey(
                                  val === SELECT_PLACEHOLDER ? "" : val,
                                )
                              }
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Value Key" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={SELECT_PLACEHOLDER}>
                                  Select value key
                                </SelectItem>
                                {availableKeys.length > 0 ? (
                                  availableKeys.map((k) => (
                                    <SelectItem key={k} value={k}>
                                      {k}
                                    </SelectItem>
                                  ))
                                ) : (
                                  <SelectItem value="none" disabled>
                                    No keys available
                                  </SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}

                      {availableKeys.length > 0 && (
                        <Button
                          onClick={handleGenerateOptions}
                          disabled={!labelKey || !valueKey}
                          className="mt-4"
                        >
                          Generate Dropdown Options
                        </Button>
                      )}
                    </div>
                  )}

                  {/* List of Added Options */}
                  {Array.isArray(tempField.values) &&
                    tempField.values.length > 0 && (
                      <div className="space-y-1 text-sm max-h-[120px] overflow-y-auto bg-lightGray p-2 rounded-lg border border-secondary">
                        {tempField.values.map((opt, idx) => (
                          <div
                            key={idx}
                            className="flex justify-between items-center p-2 rounded-lg bg-background font-mono border-2 border-secondary"
                          >
                            <span>{opt.displayName}</span>
                            <Trash
                              className="cursor-pointer text-primary"
                              size={14}
                              onClick={() => {
                                const updated = [...(tempField.values || [])];
                                updated.splice(idx, 1);
                                // Reset default to first option if exists
                                const cleaned = updated.map((opt, i) =>
                                  i === 0
                                    ? { ...opt, default: "selected" }
                                    : { ...opt, default: undefined },
                                );
                                setTempField({
                                  ...tempField,
                                  values: cleaned,
                                });
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              </>
            )}

            {tempField.fieldType === "testConnectionBtn" && (
              <div className="p-3 border-2 border-secondary rounded-lg bg-muted text-sm mt-3 mb-2">
                <label htmlFor="">
                  {meshVersion === "2.0"
                    ? "Select a settings workflow"
                    : "Select a bundle"}
                </label>
                {availableBundles.length === 0 ? (
                  <div className="border rounded-lg flex items-center justify-center p-4 text-sm text-muted-foreground mt-2 bg-background">
                    {isLogicLoading
                      ? "Loading..."
                      : meshVersion === "2.0"
                        ? "No settings workflows found"
                        : "No bundles found in Settings"}
                  </div>
                ) : (
                  <Select
                    value={String(selectedBundle || "")}
                    onValueChange={handleSelectedBundle}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          meshVersion === "2.0"
                            ? "Select a workflow"
                            : "Select a bundle"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {availableBundles.map((bundle) => (
                        <SelectItem key={bundle.id} value={String(bundle.id)}>
                          {bundle.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {meshVersion !== "2.0" &&
                  selectedBundle &&
                  availableRequests.length > 0 && (
                    <div className="mt-2">
                      <label htmlFor="">
                        Select a request to test connection{" "}
                      </label>
                      <Select
                        value={String(selectedRequest || "")}
                        onValueChange={handleSelectRequest}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a request" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableRequests.map((request) => (
                            <SelectItem
                              key={request.requestId}
                              value={String(request.requestId)}
                            >
                              {`Request - ${request.requestId}`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
              </div>
            )}

            {/* Row 5+: Placeholder, Default, InfoIcon - 2 per row */}
            <div className="flex gap-2  mb-3">
              {tempField.fieldType !== "testConnectionBtn" &&
                tempField.fieldType !== "proxy" && (
                  <>
                    <div>
                      <label htmlFor="placeholder" className="text-xs ">
                        Placeholder
                      </label>
                      <input
                        className="p-2 border rounded-lg bg-transparent border-secondary focus:ring-secondary focus:ring-1"
                        placeholder="Placeholder (optional)"
                        value={tempField.placeholder}
                        onChange={(e) =>
                          setTempField({
                            ...tempField,
                            placeholder: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label htmlFor="default" className="text-xs ">
                        Default Value
                      </label>
                      <input
                        className="p-2 border rounded-lg bg-transparent border-secondary focus:ring-secondary focus:ring-1"
                        placeholder="Default Value (optional)"
                        value={tempField.default}
                        onChange={(e) =>
                          setTempField({
                            ...tempField,
                            default: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div>
                      <label htmlFor="infoIcon" className="text-xs ">
                        Tooltip
                      </label>
                      <input
                        className="p-2 border rounded-lg bg-transparent border-secondary focus:ring-secondary focus:ring-1"
                        placeholder="Tooltip (optional)"
                        value={tempField.infoIcon}
                        onChange={(e) =>
                          setTempField({
                            ...tempField,
                            infoIcon: e.target.value,
                          })
                        }
                      />
                    </div>
                  </>
                )}
            </div>

            {tempField.fieldType === "proxy" && (
              <div className="flex flex-col gap-1 mb-3">
                <label htmlFor="infoIcon" className="text-xs ">
                  Tooltip
                </label>
                <input
                  className="p-2 border rounded-lg bg-transparent border-secondary focus:ring-secondary focus:ring-1"
                  placeholder="Tooltip (optional)"
                  value={tempField.infoIcon}
                  onChange={(e) =>
                    setTempField({ ...tempField, infoIcon: e.target.value })
                  }
                />
              </div>
            )}

            {/* Add Field button */}
            <div className="flex justify-end mt-4 gap-2">
              <Button
                onClick={clearField}
                disabled={
                  !tempField.fieldTitle &&
                  !tempField.givenName &&
                  !tempField.fieldType &&
                  !tempField.placeholder &&
                  !tempField.default &&
                  !tempField.infoIcon &&
                  (!tempField.values || tempField.values.length === 0)
                }
                className="bg-muted-foreground text-background hover:bg-muted-foreground/80 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Clear Fields
              </Button>
              <Button
                disabled={
                  !formTitle ||
                  !tempField.fieldTitle ||
                  !tempField.givenName ||
                  !tempField.fieldType ||
                  (["checkbox", "radio", "dropdown"].includes(
                    String(tempField.fieldType || ""),
                  ) &&
                    (!tempField.values || tempField.values.length === 0)) ||
                  (tempField.fieldType === "dropdown" &&
                    isFromVariable &&
                    (!selectedSettingsBundleId ||
                      !selectedVariable ||
                      !tempField.values ||
                      tempField.values.length === 0)) ||
                  (tempField.fieldType === "testConnectionBtn" &&
                    (!selectedBundle ||
                      (meshVersion !== "2.0" && !selectedRequest)))
                }
                onClick={addField}
              >
                <CirclePlus size={15} className="mr-1" />{" "}
                {editFieldId ? "Update" : "Add"} Fields
              </Button>
            </div>

            {/* List of Added Fields */}
            {fields.length > 0 && (
              <div className="mt-4 space-y-2 max-h-[220px] overflow-y-auto bg-muted p-3 rounded-lg border border-secondary">
                <h4 className="text-md">
                  Added fields of{" "}
                  <span className="font-semibold text-primary">
                    {formTitle}
                  </span>
                </h4>
                <p className="text-xs text-muted-foreground">
                  Drag fields to reorder. The saved form keeps this order.
                </p>

                {fields.map((field, idx) => {
                  const Icon = getFieldIconbyFieldType(field?.fieldType || "");
                  const isTitle = isTitleField(field);
                  return (
                    <div
                      key={field.fieldId}
                      draggable={!isTitle}
                      onDragStart={() => handleDragStart(field)}
                      onDragOver={(event) => handleDragOver(event, field)}
                      onDrop={() => handleDrop(field)}
                      onDragEnd={handleDragEnd}
                      className={`group p-2 border rounded-lg flex justify-between items-center text-sm border-secondary bg-background ${
                        dragOverFieldId === field.fieldId &&
                        draggedFieldId !== field.fieldId
                          ? "ring-2 ring-secondary"
                          : ""
                      } ${!isTitle ? "cursor-move" : "cursor-default"}`}
                    >
                      {!isTitle && (
                        <GripVertical
                          size={16}
                          className={`mr-1 text-lightGray group-hover:text-muted-foreground ${isTitle ? "cursor-default" : "cursor-move"}`}
                          aria-label="Drag to reorder"
                        />
                      )}
                      <Icon
                        size={18}
                        className={`mr-2 text-muted-foreground ${isTitle ? "cursor-default ml-4" : "cursor-move"}`}
                      />

                      <p className="flex items-center w-full justify-between">
                        <span>{field.fieldTitle} </span>
                        <span className="text-xs mr-2">
                          {field.fieldType == "testConnectionBtn" && (
                            <CustomTooltip
                              className="w-80 text-xs"
                              text="This field represents a Test Connection button. It won’t appear in the form or preview as a field but enables the Test Connection feature in preview."
                              side="left"
                            >
                              <Info size={14} />
                            </CustomTooltip>
                          )}
                        </span>
                      </p>
                      <div className="flex gap-1">
                        {/* Prevent editing/deleting Title field */}
                        {!isTitle && (
                          <>
                            <span className="bg-muted rounded-md px-2 text-gray-500 text-xs mr-1">
                              {field.fieldType}
                            </span>
                            <button
                              className="text-foreground"
                              onClick={() => editField(field.fieldId)}
                            >
                              <SquarePen size={16} />
                            </button>
                            <button
                              className="text-primary"
                              onClick={() => deleteField(idx)}
                            >
                              <Trash size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {/* Save Form Button */}
            <div className="pt-4 flex justify-end">
              <Button
                className="w-full rounded-lg "
                onClick={handleSubmit}
                disabled={
                  fields.length === 0 || !formTitle || editFieldId !== null
                }
              >
                Save Form
              </Button>
            </div>
          </div>
        </label>
      </DialogContent>
    </Dialog>
  );
};

export default AddFormDialog;
